import { OptionItem } from '@/contracts/models/survey';
import { useTranslation } from '@/hooks/useTranslation';
import { createOption } from '@/utils/optionId';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type TFunction = ReturnType<typeof useTranslation>['t'];

export interface ReorderableListProps {
  /** Options edited in place: the text is editable, the value (id) is stable */
  items: OptionItem[];
  onChange: (items: OptionItem[]) => void;
  disabled?: boolean;
  /** Hides add/remove/reorder controls but keeps text inputs editable */
  lockStructure?: boolean;
  /** Original display labels shown alongside editable fields when locked */
  originalItems?: string[];
  minItems?: number;
  itemType: 'choice' | 'row' | 'column';
  t: TFunction;
  renderLeadingIcon?: (index: number) => ReactNode;
  /** Rendered after each item's text input (e.g. branch target selector) */
  renderItemSuffix?: (index: number) => ReactNode;
  getItemPlaceholder?: (index: number) => string;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const ReorderableList: React.FC<ReorderableListProps> = ({
  items,
  onChange,
  disabled = false,
  lockStructure = false,
  originalItems,
  minItems = 1,
  itemType,
  t,
  renderLeadingIcon,
  renderItemSuffix,
  getItemPlaceholder,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const dragHandleRef = useRef<boolean>(false);
  const moveUpButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const moveDownButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pendingFocusRef = useRef<{
    type: 'up' | 'down';
    index: number;
  } | null>(null);

  const ItemType = capitalize(itemType);
  const getPlaceholder = () => t(`editor.fields.${itemType}Placeholder`);
  const getItemAriaLabel = (index: number, item: string) =>
    t(`editor.fields.${itemType}ItemAriaLabel`, {
      number: index + 1,
      value: item || getPlaceholder(),
    });
  const getGroupAriaLabel = (index: number, item: string) =>
    t(`editor.fields.${itemType}GroupAriaLabel`, {
      number: index + 1,
      value: item || getPlaceholder(),
    });
  const getDeleteAriaLabel = (index: number, item: string) =>
    t(`editor.actions.delete${ItemType}AriaLabel`, {
      number: index + 1,
      value: item || getPlaceholder(),
    });
  const getMoveUpAriaLabel = (index: number, item: string) =>
    t(`editor.actions.move${ItemType}Up`, {
      number: index + 1,
      value: item || getPlaceholder(),
    });
  const getMoveDownAriaLabel = (index: number, item: string) =>
    t(`editor.actions.move${ItemType}Down`, {
      number: index + 1,
      value: item || getPlaceholder(),
    });
  const getDeleteTooltip = () => t(`editor.validation.minimum${ItemType}s`);
  const getAddAriaLabel = () => t(`editor.actions.add${ItemType}`);
  const getInputPlaceholder = (index: number) =>
    t(`editor.fields.${itemType}PlaceholderNumbered`, { number: index + 1 });

  useEffect(() => {
    if (pendingFocusRef.current) {
      const { type, index } = pendingFocusRef.current;
      const buttonRef =
        type === 'up'
          ? moveUpButtonRefs.current[index]
          : moveDownButtonRefs.current[index];
      buttonRef?.focus();
      pendingFocusRef.current = null;
    }
  }, [items]);

  const setMoveUpButtonRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      moveUpButtonRefs.current[index] = el;
    },
    []
  );

  const setMoveDownButtonRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      moveDownButtonRefs.current[index] = el;
    },
    []
  );

  const handleItemChange = (index: number, text: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], text };
    onChange(newItems);
  };

  const handleAdd = () => {
    onChange([...items, createOption()]);
  };

  const handleRemove = (index: number) => {
    if (items.length > minItems) {
      const newItems = items.filter((_, i) => i !== index);
      onChange(newItems);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [
      newItems[index],
      newItems[index - 1],
    ];
    pendingFocusRef.current = { type: 'up', index: index - 1 };
    onChange(newItems);
  };

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [
      newItems[index + 1],
      newItems[index],
    ];
    pendingFocusRef.current = { type: 'down', index: index + 1 };
    onChange(newItems);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!dragHandleRef.current) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    onChange(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    dragHandleRef.current = false;
  };

  const handleHandleMouseDown = () => {
    dragHandleRef.current = true;
  };

  const handleHandleMouseUp = () => {
    dragHandleRef.current = false;
  };

  return (
    <div className='space-y-2'>
      {items.map((item, index) => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Drag-and-drop has keyboard alternatives
        <div
          key={item.value}
          role='group'
          aria-label={getGroupAriaLabel(index, item.text)}
          draggable={!disabled && !lockStructure}
          onDragStart={e => handleDragStart(e, index)}
          onDragOver={e => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-2 ${draggedIndex === index ? 'opacity-50' : ''}`}
        >
          {!lockStructure && (
            <Button
              icon='pi pi-minus-circle'
              rounded
              text
              severity='danger'
              onClick={() => handleRemove(index)}
              disabled={disabled || items.length <= minItems}
              aria-label={getDeleteAriaLabel(index, item.text)}
              tooltip={
                items.length <= minItems ? getDeleteTooltip() : undefined
              }
              tooltipOptions={{ position: 'top' }}
              className='flex-shrink-0 !p-1'
            />
          )}

          {renderLeadingIcon && renderLeadingIcon(index)}

          {lockStructure && originalItems?.[index] && (
            <span className='text-sm text-gray-400 flex-1 truncate'>
              {originalItems[index]}
            </span>
          )}

          <InputText
            value={item.text}
            onChange={e => handleItemChange(index, e.target.value)}
            placeholder={
              originalItems?.[index] ||
              (getItemPlaceholder
                ? getItemPlaceholder(index)
                : getInputPlaceholder(index))
            }
            disabled={disabled}
            aria-label={getItemAriaLabel(index, item.text)}
            className='flex-1'
          />

          {renderItemSuffix && renderItemSuffix(index)}

          {!disabled && !lockStructure && (
            <div className='flex items-center gap-3 flex-shrink-0 ml-2'>
              <i
                className='pi pi-bars text-gray-400 cursor-move'
                aria-hidden='true'
                role='presentation'
                onMouseDown={handleHandleMouseDown}
                onMouseUp={handleHandleMouseUp}
              />
              <div className='flex flex-col'>
                <button
                  ref={setMoveUpButtonRef(index)}
                  type='button'
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  aria-label={getMoveUpAriaLabel(index, item.text)}
                  className='reorder-btn'
                >
                  <i className='pi pi-chevron-up text-xs' />
                </button>
                <button
                  ref={setMoveDownButtonRef(index)}
                  type='button'
                  onClick={() => handleMoveDown(index)}
                  disabled={index === items.length - 1}
                  aria-label={getMoveDownAriaLabel(index, item.text)}
                  className='reorder-btn'
                >
                  <i className='pi pi-chevron-down text-xs' />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {!lockStructure && (
        <div className='flex items-center gap-2'>
          <Button
            icon='pi pi-plus-circle'
            rounded
            text
            severity='success'
            onClick={handleAdd}
            disabled={disabled}
            aria-label={getAddAriaLabel()}
            className='flex-shrink-0 !p-1'
          />

          {renderLeadingIcon && (
            <span className='opacity-50'>
              {renderLeadingIcon(items.length)}
            </span>
          )}

          <InputText
            placeholder={getAddAriaLabel()}
            disabled
            className='flex-1 bg-gray-50'
          />

          <div
            className='flex items-center gap-3 flex-shrink-0 ml-2 invisible'
            aria-hidden='true'
          >
            <i className='pi pi-bars' />
            <div className='flex flex-col'>
              <i className='pi pi-chevron-up text-xs' />
              <i className='pi pi-chevron-down text-xs' />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
